import {
    IBaseModule,
    ISearchResult,
    IDocumentImage,
    IDocumentImageSearchParam,
}from "@/store/common/Types";
import ApiService from "@/core/services/ApiService";
import { Actions, Mutations, Endpoints } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";

@Module
export default class DocumentImagesModule extends VuexModule implements IBaseModule {
    errors = {};
    images = {} as ISearchResult<IDocumentImage>;
    documentImage = {} as IDocumentImage;
    documentImageId = {};

    /**
     * Get Document Image errors
     * @returns array
    */
    get getDocumentImageErrors() {
      return this.errors;
    }  
    
    /**
     * Get search result
     * @returns ISearchResult<IDocumentImage>
    */
    get getDocumentImages(): ISearchResult<IDocumentImage> {
        return this.images;
    } 

    /**
     * Get document image
     * @returns IDocumentImage
    */
    get getDocumentImage(): IDocumentImage {
        return this.documentImage;
    }

    /**
     * Get newly-created holiday ID
     * @returns string
     */
    get getDocumentImageId() {
        return this.documentImageId;
    }

    @Mutation
    [Mutations.SET_ERROR](error) {
        this.errors = { ...error };
    }

    @Mutation
    [Mutations.SET_DOCUMENT_IMAGES](items: ISearchResult<IDocumentImage>) {
        this.images = items;    
    }

    @Mutation
    [Mutations.SET_DOCUMENT_IMAGE](documentImage: IDocumentImage) {
        this.documentImage = documentImage;
    }

    @Mutation
    [Mutations.SET_DOCUMENT_IMAGE_ID](documentImageId: string) {
      this.documentImageId = documentImageId;
    }

    @Action
    [Actions.SEARCH_DOCUMENT_IMAGES](param: IDocumentImageSearchParam) {
        if (!param) {
            return;
        } 
  
        let query = "?";

        if (param.term) {
            query += "term=" + param.term;
        }

        if (param.startDate) {
            if (query.slice(-1) !== "?") {
                query += "&";
            }
            query += "startDate=" + new Date(Date.UTC(param.startDate.getFullYear(), param.startDate.getMonth(),
                     param.startDate.getDate(), 0, 0, 0, 0)).toISOString();
        }
        
        if (param.endDate) {
            if (query.slice(-1) !== "?") {
                query += "&";
            }
            query += "endDate=" + new Date(Date.UTC(param.endDate.getFullYear(), param.endDate.getMonth(),
                     param.endDate.getDate(), 0, 0, 0, 0)).toISOString();
        }

        if (param.page != 1) {
            if (query.slice(-1) !== "?") {
                query += "&";
            }
            query += "page=" + param.page;
        }

        if (param.orderBy) {
            if (query.slice(-1) !== "?") {
                query += "&";
            }
            query += "orderBy=" + param.orderBy;
        }

        ApiService.setHeader();
        return ApiService.get(Endpoints.DocumentImages, query)
        .then(({ data }) => {
            this.context.commit(Mutations.SET_DOCUMENT_IMAGES, data);
        })
        .catch(({ response }) => {
            this.context.commit(Mutations.SET_ERROR, [
              "There was an error searching for document images.",
            ]);          
        });
    }

    @Action
    [Actions.LOAD_DOCUMENT_IMAGE](documentImageId: string) {
      ApiService.setHeader();
      return ApiService.get(Endpoints.DocumentImages, documentImageId)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_DOCUMENT_IMAGE, data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, [
              "There was an error loading the document image.",
            ]);
        });
    }

    @Action
    [Actions.CREATE_DOCUMENT_IMAGE](documentImage: IDocumentImage) {
        ApiService.setHeader();
        const req = {
            title: documentImage.title,
            fileName: documentImage.fileName,
            content: documentImage.content,
        } as any;

        return ApiService.post(Endpoints.DocumentImages, req)
        .then(({ data }) => {
            this.context.commit(Mutations.SET_DOCUMENT_IMAGE_ID, data);
            this.context.commit(Mutations.SET_ERROR, {});
        })
        .catch(({ response }) => {
            if (!response) {
            this.context.commit(Mutations.SET_ERROR, [
                "There was an error creating the document image.",
            ]);
                return;
            }
            if (response.data && response.data.errors) {
            this.context.commit(Mutations.SET_ERROR, response.data.errors);
            }
        });
    }

    @Action
    [Actions.UPDATE_DOCUMENT_IMAGE](documentImage: IDocumentImage) {
        ApiService.setHeader();
        const req = {
            title: {
                op: "Replace",
                value: documentImage.title,
            },
            fileName: {
                op: "Replace",
                value: documentImage.fileName,
            },
            content: {
                op: "Replace",
                value: documentImage.content,
            },
        } as any;

        return ApiService.patch(Endpoints.DocumentImages + "/" + documentImage.documentImageId, req)
        .then(({ data }) => {
            this.context.commit(Mutations.SET_ERROR, {});
        })
        .catch(({ response }) => {
            if (!response) {
                this.context.commit(Mutations.SET_ERROR, [
                 "There was an error updating the document image.",
                ]);
            }
            if (response.data && response.data.errors) {
                this.context.commit(Mutations.SET_ERROR, response.data.errors);
            }
        });
    }

    @Action
    [Actions.DELETE_DOCUMENT_IMAGE](imageId: string) {
      ApiService.setHeader();
      return ApiService.delete(Endpoints.DocumentImages, imageId)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_ERROR, {});
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, [
              "There was an error deleting the document image.",
            ]);
        });
    }

    @Action
    [Actions.DOWNLOAD_IMAGE_DOC](imageId: string) {
        this.context.commit(Mutations.SET_ERROR, []);
        ApiService.setHeader();        
        return ApiService.get(Endpoints.DocumentImages, imageId + '/preview')
            .then(({ data, headers }) => {
              var atData = Uint8Array.from(atob(data.value), (c) =>
                c.charCodeAt(0)
              );
              var blob = new Blob([atData], { type: data.mimeType });
              const url = window.URL.createObjectURL(blob);
              return url;
            })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, [
            "There was an error loading the document image.",
          ]);
      });
  }
}